import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Button, Form, FormControl } from "react-bootstrap";
import { useSelector } from "react-redux";
import {
  FaGraduationCap,
  FaTag,
  FaBook,
  FaClock,
  FaHourglassHalf,
  FaPlay,
  FaStop,
  FaInfoCircle,
  FaMinus,
  FaStickyNote,
  FaAngleRight,
} from "react-icons/fa";
import { MdMobileOff } from "react-icons/md";
import noSoundImg from "../../img/no_sound.png";
import http from "../../api/http";
import "./exams.styles.scss";

const NoSoundIcon = () => (
  <img src={noSoundImg} alt="silêncio" style={{ width: "1.5em", height: "1.5em", objectFit: "contain" }} />
);

const PRESET_RULES = [
  {
    text: "Proibido o uso de dispositivos eletrónicos (telemóveis, smartwatches, etc.)",
    Icon: MdMobileOff,
    subtitle: "Por favor, desligue e guarde todos os equipamentos.",
    type: "ban",
  },
  {
    text: "Exame sem consulta de materiais externos (livros, apontamentos)",
    Icon: FaBook,
    subtitle: "Só é permitido consultar materiais fornecidos pelo professor exclusivamente para o exame",
    type: "ban",
  },
  {
    text: "Mantenho o silêncio total durante o exame",
    Icon: NoSoundIcon,
    subtitle:
      "Respeite os outros estudantes e os vigilantes durante todo o exame.",
    type: "sil",
  },
];

const ExamsPage = () => {
  const employeeId = useSelector((state) => state.employee.employeeId);
  const employeeName = useSelector((state) => state.employee.employeeName);

  // ── fetch / list ─────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [titleText, setTitleText] = useState("");
  const [exams, setExams] = useState([]);

  // ── selected exam form ───────────────────────────────────────────────────
  const [selectedExam, setSelectedExam] = useState(null);
  const [courseUnit, setCourseUnit] = useState("");
  const [curso, setCurso] = useState("");
  const [room, setRoom] = useState("");
  const [startTime, setStartTime] = useState("");
  const [durationMinutes, setDurationMinutes] = useState(120);
  const [selectedRules, setSelectedRules] = useState(
    PRESET_RULES.map(function (p) {
      return p.text;
    })
  );
  const [customRule, setCustomRule] = useState("");
  const [notes, setNotes] = useState("");

  // ── saving state ─────────────────────────────────────────────────────────
  const [savingNotes, setSavingNotes] = useState(false);
  const [savingProctors, setSavingProctors] = useState(false);
  const [savingInstructor, setSavingInstructor] = useState(false);
  const [savingDuration, setSavingDuration] = useState(false);

  // ── ad-hoc (manual) exam ─────────────────────────────────────────────────
  const [showAdHocForm, setShowAdHocForm] = useState(false);
  const [adHocForm, setAdHocForm] = useState({
    courseUnit: "",
    room: "",
    startTime: "",
    durationMinutes: 120,
  });

  // ── proctors ─────────────────────────────────────────────────────────────
  const [allEmployees, setAllEmployees] = useState([]);
  const [proctorSearch, setProctorSearch] = useState("");
  const [selectedProctorIds, setSelectedProctorIds] = useState([]);

  // ── projection / clock ───────────────────────────────────────────────────
  const [isProjectionMode, setIsProjectionMode] = useState(false);
  // Stored times use UTC-as-local (08:50+00:00 means the exam is at 08:50 local).
  // To compare correctly we shift `now` by the timezone offset so it lives in
  // the same frame: local 09:22 (UTC+1) → fake-UTC 09:22 instead of real 08:22.
  const [now, setNow] = useState(function () {
    return toUTCAsLocal(new Date());
  });

  // ── role: is the current user the instructor of the selected exam? ────────
  const isInstructor = useMemo(
    function () {
      return (
        selectedExam != null &&
        (selectedExam.isAdHoc ||
          String(selectedExam.requesterId) === String(employeeId))
      );
    },
    [selectedExam, employeeId]
  );

  // ── fetch exams ───────────────────────────────────────────────────────────
  const fetchExams = useCallback(
    async function () {
      if (!employeeId) {
        setExams([]);
        return;
      }

      setLoading(true);
      setTitleText("");

      try {
        const currentDate = new Date().toISOString();
        const res = await http.get(
          "reservation/exams/" + employeeId + "?dates=" + currentDate
        );

        var reservations =
          res && res.data && res.data.data && res.data.data.reservationData
            ? res.data.data.reservationData
            : [];

        setExams(reservations.map(mapReservationToExam));
      } catch (err) {
        setTitleText(
          err && err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : "Algo inesperado aconteceu, tente novamente mais tarde!"
        );
      } finally {
        setLoading(false);
      }
    },
    [employeeId]
  );

  useEffect(
    function () {
      if (!employeeId) return;
      fetchExams();
    },
    [employeeId, fetchExams]
  );

  // ── fetch employees for proctor picker ────────────────────────────────────
  useEffect(
    function () {
      if (!employeeId) return;

      http
        .get("employee/all")
        .then(function (res) {
          var employees =
            res && res.data && res.data.data && res.data.data.employees
              ? res.data.data.employees
              : [];
          setAllEmployees(employees);
        })
        .catch(function () {});
    },
    [employeeId]
  );

  // ── clock tick (always active so countdown stays live in all views) ─────────
  useEffect(function () {
    var interval = setInterval(function () {
      setNow(toUTCAsLocal(new Date()));
    }, 1000);
    return function () {
      clearInterval(interval);
    };
  }, []);

  // ── select exam ───────────────────────────────────────────────────────────
  var handleSelectExam = function (exam) {
    setSelectedExam(exam);
    setCourseUnit(exam.courseUnitDisplay);
    setCurso(exam.cursoDisplay);
    setRoom(exam.room);
    setStartTime(exam.startTime); // keep original ISO string so new Date() parses it as UTC
    setDurationMinutes(exam.durationMinutes || 120);
    setSelectedRules(
      exam.rules.length > 0
        ? exam.rules
        : PRESET_RULES.map(function (p) {
            return p.text;
          })
    );
    setCustomRule("");
    setNotes(exam.notes || "");
    setSelectedProctorIds(
      (exam.proctors || []).map(function (id) {
        return String(id);
      })
    );
    setIsProjectionMode(false);
  };

  // ── rules (instructor only) ───────────────────────────────────────────────
  var handleRuleToggle = function (rule) {
    setSelectedRules(function (prev) {
      return prev.includes(rule)
        ? prev.filter(function (r) {
            return r !== rule;
          })
        : prev.concat([rule]);
    });
  };

  var handleAddCustomRule = function () {
    var trimmed = customRule.trim();
    if (!trimmed || selectedRules.includes(trimmed)) {
      setCustomRule("");
      return;
    }
    setSelectedRules(function (prev) {
      return prev.concat([trimmed]);
    });
    setCustomRule("");
  };

  // ── proctors (instructor only) ────────────────────────────────────────────
  var handleToggleProctor = function (empId) {
    setSelectedProctorIds(function (prev) {
      return prev.includes(empId)
        ? prev.filter(function (id) {
            return id !== empId;
          })
        : prev.concat([empId]);
    });
  };

  // ── generic save to /info endpoint ───────────────────────────────────────
  var saveExamInfo = async function (fields) {
    if (!selectedExam || selectedExam.isAdHoc) return;
    await http.patch("reservation/exams/" + selectedExam._id + "/info", fields);
  };

  // ── save instructor section (UC, Curso, rules, proctors) ─────────────────
  var handleSaveInstructorInfo = async function () {
    setSavingInstructor(true);
    try {
      await saveExamInfo({
        courseUnitOverride:
          courseUnit !== selectedExam.courseUnit ? courseUnit : undefined,
        cursoOverride: curso !== selectedExam.abrevCurso ? curso : undefined,
        rules: selectedRules,
        proctors: selectedProctorIds,
      });
      setSelectedExam(function (prev) {
        return Object.assign({}, prev, {
          courseUnitDisplay: courseUnit,
          cursoDisplay: curso,
          rules: selectedRules,
          proctors: selectedProctorIds,
        });
      });
    } catch (err) {
      alert("Erro ao guardar informações. Tente novamente.");
    } finally {
      setSavingInstructor(false);
    }
  };

  // ── save proctors only (used from tag list quick-remove) ─────────────────
  var handleSaveProctors = async function () {
    setSavingProctors(true);
    try {
      await saveExamInfo({ proctors: selectedProctorIds });
      setSelectedExam(function (prev) {
        return Object.assign({}, prev, { proctors: selectedProctorIds });
      });
    } catch (err) {
      alert("Erro ao guardar vigilantes. Tente novamente.");
    } finally {
      setSavingProctors(false);
    }
  };

  // ── save duration ─────────────────────────────────────────────────────────
  var handleSaveDuration = async function () {
    setSavingDuration(true);
    try {
      await saveExamInfo({ durationOverrideMinutes: durationMinutes });
    } catch (err) {
      alert("Erro ao guardar duração. Tente novamente.");
    } finally {
      setSavingDuration(false);
    }
  };

  // ── add extra time (usable in projection mode) ────────────────────────────
  var handleAddExtraTime = async function (extraMinutes) {
    var newDuration = durationMinutes + extraMinutes;
    setDurationMinutes(newDuration);
    try {
      await saveExamInfo({ durationOverrideMinutes: newDuration });
    } catch (err) {
      // non-blocking — the UI already updated; silently ignore
    }
  };

  // ── save notes ────────────────────────────────────────────────────────────
  var handleSaveNotes = async function () {
    setSavingNotes(true);
    try {
      await saveExamInfo({ notes: notes });
      setSelectedExam(function (prev) {
        return Object.assign({}, prev, { notes: notes });
      });
    } catch (err) {
      alert("Erro ao guardar notas. Tente novamente.");
    } finally {
      setSavingNotes(false);
    }
  };

  // ── start a manual ad-hoc exam (no reservation required) ─────────────────
  var handleStartAdHoc = function () {
    var trimmedCU = adHocForm.courseUnit.trim();
    if (!trimmedCU || !adHocForm.startTime) return;
    var now = new Date();
    var datePart =
      now.getUTCFullYear() +
      "-" +
      String(now.getUTCMonth() + 1).padStart(2, "0") +
      "-" +
      String(now.getUTCDate()).padStart(2, "0");
    var isoStart = datePart + "T" + adHocForm.startTime + ":00.000Z";
    var dur = adHocForm.durationMinutes || 120;

    var syntheticExam = {
      _id: null,
      isAdHoc: true,
      courseUnit: trimmedCU,
      courseUnitDisplay: trimmedCU,
      cursoDisplay: "",
      room: adHocForm.room.trim(),
      startTime: isoStart,
      durationMinutes: dur,
      requesterId: employeeId,
      requester: employeeName,
      rules: [],
      notes: "",
      proctors: [],
      semestre: null,
      ano: null,
    };

    setSelectedExam(syntheticExam);
    setCourseUnit(trimmedCU);
    setCurso("");
    setRoom(adHocForm.room.trim());
    setStartTime(isoStart);
    setDurationMinutes(dur);
    setSelectedRules(PRESET_RULES.map(function (p) { return p.text; }));
    setCustomRule("");
    setNotes("");
    setSelectedProctorIds([]);
    setIsProjectionMode(false);
    setShowAdHocForm(false);
  };

  // ── derived time values ───────────────────────────────────────────────────
  var startDateObj = useMemo(
    function () {
      if (!startTime) return null;
      return new Date(startTime);
    },
    [startTime]
  );

  var endDateObj = useMemo(
    function () {
      if (!startDateObj) return null;
      return new Date(startDateObj.getTime() + durationMinutes * 60000);
    },
    [startDateObj, durationMinutes]
  );

  var hasStarted = useMemo(
    function () {
      if (!startDateObj) return false;
      return now.getTime() >= startDateObj.getTime();
    },
    [now, startDateObj]
  );

  var hasEnded = useMemo(
    function () {
      if (!endDateObj) return false;
      return now.getTime() >= endDateObj.getTime();
    },
    [now, endDateObj]
  );

  var remainingMs = useMemo(
    function () {
      if (!endDateObj || !startDateObj) return 0;
      if (!hasStarted) {
        // Count down to when the exam starts
        var toStart = startDateObj.getTime() - now.getTime();
        return toStart > 0 ? toStart : 0;
      }
      // Count down to when the exam ends
      var toEnd = endDateObj.getTime() - now.getTime();
      return toEnd > 0 ? toEnd : 0;
    },
    [endDateObj, startDateObj, now, hasStarted]
  );

  var formattedRemaining = useMemo(
    function () {
      return formatRemainingTime(remainingMs);
    },
    [remainingMs]
  );

  // ── projection mode ───────────────────────────────────────────────────────
  var enterProjectionMode = function () {
    setIsProjectionMode(true);
    if (document.documentElement.requestFullscreen) {
      document.documentElement.requestFullscreen().catch(function () {});
    }
  };

  var exitProjectionMode = function () {
    setIsProjectionMode(false);
    if (document.exitFullscreen) {
      document.exitFullscreen().catch(function () {});
    }
  };

  // ── filtered employees (exclude self) ─────────────────────────────────────
  var filteredEmployees = useMemo(
    function () {
      var q = proctorSearch.trim().toLowerCase();
      return allEmployees.filter(function (emp) {
        return (
          String(emp._id) !== String(employeeId) &&
          (q === "" || emp.name.toLowerCase().includes(q))
        );
      });
    },
    [allEmployees, proctorSearch, employeeId]
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // Projection view
  // ═══════════════════════════════════════════════════════════════════════════
  if (isProjectionMode && selectedExam) {
    var hasRules = selectedRules.length > 0;
    var hasNotes = notes.trim() !== "";

    var progressPercent = 0;
    if (startDateObj && endDateObj) {
      if (hasEnded) {
        progressPercent = 100;
      } else if (hasStarted) {
        var elapsed = now.getTime() - startDateObj.getTime();
        var total = endDateObj.getTime() - startDateObj.getTime();
        progressPercent = Math.min(100, Math.round((elapsed / total) * 100));
      }
    }

    var dateDisplay = now.toLocaleString("pt-PT", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    });

    return (
      <div className="examProjectionWrapper">
        {/* ── Header ── */}
        <div className="examProjectionHeader">
          <span className="examProjectionHeaderLabel">
            Informações do Exame — {room || "Universidade"}
          </span>
          <div
            style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}
          >
            <Button
              variant="outline-light"
              size="sm"
              onClick={exitProjectionMode}
            >
              Sair
            </Button>
            <div className="examProjectionHeaderLogo">
              <FaGraduationCap />
            </div>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="examProjectionBody">
          {/* Top row: subject card + timer card */}
          <div className="examProjectionTopRow">
            {/* Subject card */}
            <div className="examProjectionCard">
              <div className="examProjectionCardLabel">
                <FaTag /> Identificação da Unidade Curricular
              </div>
              <div className="examProjectionSubjectRow">
                <div className="examProjectionSubjectCircle">
                  <FaGraduationCap />
                </div>
                <div className="examProjectionTitle">{courseUnit}</div>
              </div>
              {curso ? (
                <div className="examProjectionCourseRow">
                  <span className="examProjectionCourseIcon">
                    <FaBook />
                  </span>
                  <span className="examProjectionCourseText">
                    {"Curso: " + curso}
                    {selectedExam.semestre
                      ? " · Semestre: " + selectedExam.semestre + "º"
                      : ""}
                    {selectedExam.ano
                      ? " · Ano: " + selectedExam.ano + "º"
                      : ""}
                  </span>
                </div>
              ) : null}
            </div>

            {/* Timer card */}
            <div className="examProjectionCard">
              <div className="examProjectionCardLabel">
                <FaClock /> Controlo de Tempo
              </div>

              <div className="examProjectionTimerGrid">
                <div className="examProjectionTimerBlock">
                  <div className="examProjectionTimerLabel">Hora Atual</div>
                  <div className="examProjectionCurrentTime">
                    <FaClock style={{ fontSize: "0.55em", color: "#2a5080", verticalAlign: "middle", marginRight: "0.2em" }} />
                    {formatTimeOnly(now)}
                  </div>
                  <div className="examProjectionDateDisplay">{dateDisplay}</div>
                </div>
                <div className="examProjectionTimerBlock">
                  <div className="examProjectionTimerLabel">
                    {hasEnded
                      ? "Exame Terminado"
                      : hasStarted
                      ? "Tempo Restante"
                      : "Começa Em"}
                  </div>
                  <div
                    className={
                      "examProjectionRemaining" +
                      (hasEnded ? " examProjectionRemaining--ended" : "")
                    }
                  >
                    <FaHourglassHalf style={{ fontSize: "0.45em", color: "gray", verticalAlign: "middle" }} />{" "}
                    {hasEnded ? "00:00" : formattedRemaining}
                  </div>
                  <div className="examProjectionProgressBar">
                    <div
                      className="examProjectionProgressFill"
                      style={{ width: progressPercent + "%" }}
                    />
                  </div>
                </div>
              </div>

              <div className="examProjectionTimeDivider" />

              <div className="examProjectionTimeSummary">
                <div className="examProjectionTimeSummaryBlock">
                  <div className="examProjectionTimeSummaryLabel">
                    <FaPlay /> Hora de Início
                  </div>
                  <div className="examProjectionTimeSummaryValue">
                    {startDateObj ? formatTimeOnly(startDateObj) : "—"}
                  </div>
                </div>
                <div className="examProjectionTimeSummaryBlock">
                  <div className="examProjectionTimeSummaryLabel">
                    <FaHourglassHalf /> Duração
                  </div>
                  <div className="examProjectionTimeSummaryValue">
                    {durationMinutes} min
                  </div>
                </div>
                <div className="examProjectionTimeSummaryBlock">
                  <div className="examProjectionTimeSummaryLabel">
                    <FaStop /> Hora de Fim
                  </div>
                  <div className="examProjectionTimeSummaryValue">
                    {endDateObj ? formatTimeOnly(endDateObj) : "—"}
                  </div>
                </div>
              </div>

              <div className="examProjectionExtraTime">
                <span>Tempo extra:</span>
                {[5, 10, 15, 30].map(function (mins) {
                  return (
                    <Button
                      key={mins}
                      size="sm"
                      variant="outline-secondary"
                      onClick={function () {
                        handleAddExtraTime(mins);
                      }}
                    >
                      +{mins} min
                    </Button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Rules card */}
          {hasRules ? (
            <div className="examProjectionCard">
              <div className="examProjectionCardLabel">
                <FaInfoCircle /> Regras e Restrições Importantes
              </div>
              <div className="examProjectionRulesList">
                {selectedRules.map(function (rule, i) {
                  var preset = PRESET_RULES.find(function (p) {
                    return p.text === rule;
                  });
                  return (
                    <div key={i} className="examProjectionRuleItem">
                      <div
                        className={
                          "examProjectionRuleCircle" +
                          (preset ? " " + preset.type : " custom")
                        }
                      >
                        {preset ? <preset.Icon /> : <FaMinus />}
                      </div>
                      <div>
                        <strong className="examProjectionRuleTitle">
                          {preset ? preset.text : rule}
                        </strong>
                        {preset && preset.subtitle ? (
                          <p className="examProjectionRuleSubtitle">
                            {preset.subtitle}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          {/* Notes card */}
          {hasNotes
            ? (function () {
                var noteLines = notes.split("\n").filter(function (line) {
                  return line.trim() !== "";
                });
                var ruleRows = Math.ceil(selectedRules.length / 3);
                var maxNoteRows = Math.max(1, 3 - ruleRows);
                var cols = Math.min(
                  Math.max(1, Math.ceil(noteLines.length / maxNoteRows)),
                  6
                );
                var actualRows = Math.ceil(noteLines.length / cols);
                var gap =
                  actualRows <= 2
                    ? "clamp(8px, 1.2vw, 18px)"
                    : actualRows <= 3
                    ? "5px"
                    : "3px";
                var textFs =
                  actualRows <= 1
                    ? "clamp(1.05rem, 1.9vw, 1.6rem)"
                    : actualRows <= 2
                    ? "clamp(0.95rem, 1.6vw, 1.35rem)"
                    : actualRows <= 3
                    ? "clamp(0.85rem, 1.4vw, 1.15rem)"
                    : "clamp(0.78rem, 1.2vw, 1rem)";
                var lineH = actualRows <= 2 ? 1.5 : 1.25;
                var cardPad =
                  actualRows >= 3
                    ? "clamp(8px, 1vh, 14px) clamp(10px, 1.5vw, 22px)"
                    : undefined;
                return (
                  <div
                    className="examProjectionCard"
                    style={cardPad ? { padding: cardPad } : undefined}
                  >
                    <div className="examProjectionCardLabel">
                      <FaStickyNote /> Observações
                    </div>
                    <div
                      className="examProjectionNotesList"
                      style={{
                        gridTemplateColumns: "repeat(" + cols + ", 1fr)",
                        gap: gap,
                      }}
                    >
                      {noteLines.map(function (line, i) {
                        return (
                          <div key={i} className="examProjectionNoteItem">
                            <FaAngleRight
                              className="examProjectionNoteIcon"
                              style={{ fontSize: textFs, marginTop: "0.15em" }}
                            />
                            <span
                              className="examProjectionNoteText"
                              style={{ fontSize: textFs, lineHeight: lineH }}
                            >
                              {line}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()
            : null}
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Normal view
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div className="pageWrapper examPageWrapper">
      <h3 style={{ width: "100%", marginTop: "1rem" }}>Página de Exames</h3>

      <div className="examTopBar">
        <span>
          <strong>Docente:</strong> {employeeName}
        </span>

        <Button
          variant="primary"
          onClick={fetchExams}
          disabled={loading || !employeeId}
        >
          {loading ? "A procurar..." : "Atualizar exames"}
        </Button>
      </div>

      {titleText ? <div className="examErrorBox">{titleText}</div> : null}

      {/* ── Exam list ── */}
      <div style={{ marginTop: "2rem" }}>
        <h4>Exames encontrados</h4>

        {!loading && exams.length === 0 ? (
          <div>Nenhum exame encontrado para o momento atual.</div>
        ) : null}

        {exams.map(function (exam) {
          var isSelected = selectedExam && selectedExam._id === exam._id;
          return (
            <div
              className={
                "resultsWrapper examResultWrapper" +
                (isSelected ? " examResultWrapper--selected" : "")
              }
              key={exam._id}
            >
              <div className="examResultMain">
                <span>
                  <strong>{exam.courseUnitDisplay}</strong>
                </span>
                {exam.cursoDisplay ? (
                  <span style={{ color: "#666", fontSize: "0.9rem" }}>
                    {exam.cursoDisplay}
                    {exam.ano ? " · " + exam.ano + "º ano" : ""}
                    {exam.semestre ? " · " + exam.semestre + "º semestre" : ""}
                  </span>
                ) : null}
                <span>Sala: {exam.room}</span>
                <span>
                  Início: {formatDateTimeDisplay(new Date(exam.startTime))}
                </span>
                <span>Duração prevista: {exam.durationMinutes} min</span>
              </div>

              <div>
                <Button
                  size="sm"
                  variant={isSelected ? "primary" : "outline-primary"}
                  onClick={function () {
                    handleSelectExam(exam);
                  }}
                >
                  {isSelected ? "Selecionado" : "Confirmar"}
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Manual exam section ── */}
      {!selectedExam ? (
        <div style={{ marginTop: "1.5rem" }}>
          {!showAdHocForm ? (
            <Button
              variant="outline-secondary"
              onClick={function () {
                var now = new Date();
                var t =
                  String(now.getHours()).padStart(2, "0") +
                  ":" +
                  String(now.getMinutes()).padStart(2, "0");
                setAdHocForm({ courseUnit: "", room: "", startTime: t, durationMinutes: 120 });
                setShowAdHocForm(true);
              }}
            >
              Iniciar exame manual
            </Button>
          ) : (
            <div className="examAdHocForm">
              <h5 style={{ marginBottom: "1rem" }}>Exame Manual</h5>
              <Form.Group style={{ marginBottom: "0.75rem", maxWidth: 480 }}>
                <Form.Label><strong>Unidade Curricular</strong></Form.Label>
                <Form.Control
                  type="text"
                  value={adHocForm.courseUnit}
                  placeholder="Ex: Sistemas de Informação"
                  onChange={function (e) {
                    var val = e.target.value;
                    setAdHocForm(function (prev) {
                      return Object.assign({}, prev, { courseUnit: val });
                    });
                  }}
                />
              </Form.Group>
              <Form.Group style={{ marginBottom: "0.75rem", maxWidth: 480 }}>
                <Form.Label><strong>Sala</strong> <span style={{ fontWeight: 400, color: "#888" }}>(opcional)</span></Form.Label>
                <Form.Control
                  type="text"
                  value={adHocForm.room}
                  placeholder="Ex: A201"
                  onChange={function (e) {
                    var val = e.target.value;
                    setAdHocForm(function (prev) {
                      return Object.assign({}, prev, { room: val });
                    });
                  }}
                />
              </Form.Group>
              <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", maxWidth: 480 }}>
                <Form.Group style={{ flex: 1, minWidth: 140 }}>
                  <Form.Label><strong>Hora de início</strong></Form.Label>
                  <Form.Control
                    type="time"
                    value={adHocForm.startTime}
                    onChange={function (e) {
                      var val = e.target.value;
                      setAdHocForm(function (prev) {
                        return Object.assign({}, prev, { startTime: val });
                      });
                    }}
                  />
                </Form.Group>
                <Form.Group style={{ flex: 1, minWidth: 140 }}>
                  <Form.Label><strong>Duração (min)</strong></Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    value={adHocForm.durationMinutes}
                    onChange={function (e) {
                      var val = Math.max(1, parseInt(e.target.value, 10) || 1);
                      setAdHocForm(function (prev) {
                        return Object.assign({}, prev, { durationMinutes: val });
                      });
                    }}
                  />
                </Form.Group>
              </div>
              <div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem" }}>
                <Button
                  variant="primary"
                  onClick={handleStartAdHoc}
                  disabled={!adHocForm.courseUnit.trim() || !adHocForm.startTime}
                >
                  Iniciar
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={function () { setShowAdHocForm(false); }}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : null}

      {/* ── Selected exam form ── */}
      {selectedExam ? (
        <div className="examFormWrapper">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <h4>{selectedExam.isAdHoc ? "Exame manual" : "Exame confirmado"}</h4>
            {selectedExam.isAdHoc ? (
              <Button
                variant="outline-danger"
                size="sm"
                onClick={function () {
                  setSelectedExam(null);
                  setIsProjectionMode(false);
                }}
              >
                Cancelar exame
              </Button>
            ) : null}
          </div>

          {/* Role badge */}
          <div className="examRoleBadge">
            {isInstructor ? (
              <span className="examRoleBadge--instructor">
                Docente responsável
              </span>
            ) : (
              <span className="examRoleBadge--proctor">Vigilante</span>
            )}
          </div>

          {/* ── Unidade Curricular & Curso ── */}
          {isInstructor ? (
            <div style={{ marginBottom: "1rem" }}>
              <Form.Group style={{ marginBottom: "0.75rem", maxWidth: 480 }}>
                <Form.Label>
                  <strong>Unidade Curricular</strong>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={courseUnit}
                  onChange={function (e) {
                    setCourseUnit(e.target.value);
                  }}
                />
              </Form.Group>
              <Form.Group style={{ marginBottom: "0.75rem", maxWidth: 480 }}>
                <Form.Label>
                  <strong>Curso</strong>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={curso}
                  onChange={function (e) {
                    setCurso(e.target.value);
                  }}
                />
              </Form.Group>
            </div>
          ) : (
            <div className="examResultMain" style={{ marginBottom: "1rem" }}>
              <span>
                <strong>Unidade Curricular:</strong> {courseUnit}
              </span>
              {curso ? (
                <span style={{ color: "#555" }}>
                  <strong>Curso:</strong> {curso}
                  {selectedExam.ano ? " · " + selectedExam.ano + "º ano" : ""}
                  {selectedExam.semestre
                    ? " · " + selectedExam.semestre + "º semestre"
                    : ""}
                </span>
              ) : null}
              <span>
                <strong>Docente:</strong> {selectedExam.requester}
              </span>
            </div>
          )}

          <div className="examResultMain" style={{ marginBottom: "1rem" }}>
            <span>
              <strong>Sala:</strong> {room}
            </span>
          </div>

          {/* Duration (editable by both) */}
          <Form.Group style={{ marginBottom: "0.5rem", maxWidth: 300 }}>
            <Form.Label>
              <strong>Duração (minutos)</strong>
            </Form.Label>
            <Form.Control
              type="number"
              min={1}
              value={durationMinutes}
              onChange={function (e) {
                setDurationMinutes(
                  Math.max(1, parseInt(e.target.value, 10) || 1)
                );
              }}
            />
          </Form.Group>
          {!selectedExam.isAdHoc ? (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleSaveDuration}
              disabled={savingDuration}
              style={{ marginBottom: "1.5rem" }}
            >
              {savingDuration ? "A guardar..." : "Guardar duração"}
            </Button>
          ) : null}

          {/* Time summary */}
          <div className="examResultMain" style={{ marginBottom: "1.5rem" }}>
            <span>
              <strong>Início:</strong>{" "}
              {startDateObj ? formatDateTimeDisplay(startDateObj) : "—"}
            </span>
            <span>
              <strong>Fim:</strong>{" "}
              {endDateObj ? formatDateTimeDisplay(endDateObj) : "—"}
            </span>
            <span>
              <strong>Tempo restante:</strong> {formattedRemaining}
            </span>
          </div>

          {/* Rules (instructor editable / proctor read-only) */}
          {isInstructor ? (
            <div style={{ marginBottom: "0.5rem" }}>
              <strong>Regras do exame</strong>
              <div className="examRulesBox">
                {PRESET_RULES.map(function (preset) {
                  return (
                    <Form.Check
                      key={preset.text}
                      type="checkbox"
                      label={
                        <>
                          <preset.Icon
                            style={{ marginRight: 4, verticalAlign: "middle" }}
                          />{" "}
                          {preset.text}
                        </>
                      }
                      checked={selectedRules.includes(preset.text)}
                      onChange={function () {
                        handleRuleToggle(preset.text);
                      }}
                    />
                  );
                })}
                {selectedRules
                  .filter(function (r) {
                    return !PRESET_RULES.some(function (p) {
                      return p.text === r;
                    });
                  })
                  .map(function (rule) {
                    return (
                      <div key={rule} className="examCustomRule">
                        <Form.Check
                          type="checkbox"
                          label={rule}
                          checked
                          onChange={function () {
                            handleRuleToggle(rule);
                          }}
                        />
                      </div>
                    );
                  })}
              </div>
              <div className="examAddRuleRow">
                <FormControl
                  placeholder="Adicionar outra regra..."
                  value={customRule}
                  onChange={function (e) {
                    setCustomRule(e.target.value);
                  }}
                  onKeyDown={function (e) {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddCustomRule();
                    }
                  }}
                  style={{ maxWidth: 320 }}
                />
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleAddCustomRule}
                  style={{ marginLeft: "0.5rem" }}
                >
                  Adicionar
                </Button>
              </div>
            </div>
          ) : selectedRules.length > 0 ? (
            <div style={{ marginBottom: "1.5rem" }}>
              <strong>Regras do exame</strong>
              <ul style={{ marginTop: "0.5rem" }}>
                {selectedRules.map(function (rule, i) {
                  var preset = PRESET_RULES.find(function (p) {
                    return p.text === rule;
                  });
                  return (
                    <li key={i}>
                      {preset ? (
                        <>
                          <preset.Icon
                            style={{ marginRight: 4, verticalAlign: "middle" }}
                          />
                        </>
                      ) : null}
                      {rule}
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}

          {/* Proctor assignment (instructor only) */}
          {isInstructor ? (
            <div style={{ marginBottom: "0.5rem" }}>
              <strong>Vigilantes</strong>
              <p
                style={{
                  color: "#666",
                  fontSize: "0.85rem",
                  margin: "0.25rem 0 0.5rem",
                }}
              >
                Os vigilantes selecionados poderão aceder a esta página para
                consultar e editar a duração e as notas do exame.
              </p>

              {selectedProctorIds.length > 0 ? (
                <div className="examProctorSelectedList">
                  {selectedProctorIds.map(function (id) {
                    var emp = allEmployees.find(function (e) {
                      return String(e._id) === id;
                    });
                    return emp ? (
                      <span key={id} className="examProctorTag">
                        {emp.name}
                        <button
                          className="examProctorTagRemove"
                          onClick={function () {
                            handleToggleProctor(id);
                          }}
                          title="Remover"
                        >
                          ×
                        </button>
                      </span>
                    ) : null;
                  })}
                </div>
              ) : (
                <p style={{ color: "#999", fontSize: "0.85rem" }}>
                  Nenhum vigilante selecionado.
                </p>
              )}

              <FormControl
                placeholder="Pesquisar utilizador..."
                value={proctorSearch}
                onChange={function (e) {
                  setProctorSearch(e.target.value);
                }}
                style={{ maxWidth: 320, marginBottom: "0.5rem" }}
              />

              <div className="examProctorPickerList">
                {filteredEmployees.slice(0, 20).map(function (emp) {
                  var selected = selectedProctorIds.includes(String(emp._id));
                  return (
                    <div
                      key={emp._id}
                      className={
                        "examProctorPickerItem" + (selected ? " selected" : "")
                      }
                      onClick={function () {
                        handleToggleProctor(String(emp._id));
                      }}
                    >
                      <span className="examProctorPickerCheck">
                        {selected ? "✓" : ""}
                      </span>
                      <span>{emp.name}</span>
                    </div>
                  );
                })}
                {filteredEmployees.length === 0 ? (
                  <p style={{ color: "#999", fontSize: "0.85rem", margin: 0 }}>
                    Nenhum utilizador encontrado.
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}

          {/* Instructor save button — saves UC, Curso, rules and proctors at once */}
          {isInstructor && !selectedExam.isAdHoc ? (
            <div style={{ marginBottom: "1.5rem" }}>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleSaveInstructorInfo}
                disabled={savingInstructor}
                style={{ marginTop: "0.75rem" }}
              >
                {savingInstructor
                  ? "A guardar..."
                  : "Guardar informações do exame"}
              </Button>
              <p
                style={{
                  color: "#888",
                  fontSize: "0.8rem",
                  marginTop: "0.3rem",
                }}
              >
                Guarda Unidade Curricular, Curso, regras e vigilantes.
              </p>
            </div>
          ) : null}

          {/* Notas (editable by both) */}
          <div style={{ marginBottom: "1.5rem" }}>
            <Form.Group>
              <Form.Label>
                <strong>Observações</strong>
              </Form.Label>
              <p
                style={{
                  color: "#666",
                  fontSize: "0.85rem",
                  margin: "0 0 0.4rem",
                }}
              >
                Registe aqui erros ou informações relevantes encontrados durante
                a realização do exame.
              </p>
              <Form.Control
                as="textarea"
                rows={4}
                value={notes}
                onChange={function (e) {
                  setNotes(e.target.value);
                }}
                placeholder="Ex: Erro na questão 3 — o enunciado deve ler X em vez de Y."
                style={{ maxWidth: 520 }}
              />
            </Form.Group>
            {!selectedExam.isAdHoc ? (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleSaveNotes}
                disabled={savingNotes}
                style={{ marginTop: "0.5rem" }}
              >
                {savingNotes ? "A guardar..." : "Guardar observações"}
              </Button>
            ) : null}
          </div>

          {/* Actions */}
          <div className="examActionButtons">
            <Button variant="success" onClick={enterProjectionMode}>
              Modo Projeção (Ecrã Inteiro)
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

// ── helpers ───────────────────────────────────────────────────────────────────

function mapReservationToExam(reservation) {
  var start = new Date(reservation.timeStart);
  var end = new Date(reservation.timeEnd);

  var examInfo = reservation.examInfo || {};

  // Resolve course unit: examInfo override → sumarios → description fallback
  var baseCourseName = reservation.nomeDisciplina
    ? reservation.nomeDisciplina
    : reservation.description && reservation.description.trim() !== ""
    ? reservation.description
    : "Exame";

  var courseUnitDisplay = examInfo.courseUnitOverride || baseCourseName;
  var cursoDisplay = examInfo.cursoOverride || reservation.abrevCurso || "";

  // Resolve duration: examInfo override → calculated from timeStart/timeEnd
  var calculatedDuration = getDurationInMinutes(start, end);
  var durationMinutes =
    examInfo.durationOverrideMinutes != null
      ? examInfo.durationOverrideMinutes
      : calculatedDuration;

  // Resolve rules: stored rules take priority, otherwise default to empty (UI will apply defaults)
  var rules = examInfo.rules && examInfo.rules.length > 0 ? examInfo.rules : [];

  // Resolve proctors: examInfo takes priority over legacy flat field
  var proctors =
    examInfo.proctors && examInfo.proctors.length > 0
      ? examInfo.proctors
      : reservation.proctors || [];

  // Resolve notes: examInfo takes priority over legacy flat field
  var notes =
    examInfo.notes != null ? examInfo.notes : reservation.examNotes || "";

  return {
    _id: reservation._id,
    courseUnit: baseCourseName,
    courseUnitDisplay: courseUnitDisplay,
    cursoDisplay: cursoDisplay,
    nomeDisciplina: reservation.nomeDisciplina || null,
    abrevDisciplina: reservation.abrevDisciplina || null,
    abrevCurso: reservation.abrevCurso || null,
    ano: reservation.ano || null,
    semestre: reservation.semestre || null,
    room: reservation.spaceName || reservation.nomeSala || "",
    startTime: reservation.timeStart,
    endTime: reservation.timeEnd,
    durationMinutes: durationMinutes,
    requester: reservation.requester || "",
    requesterId: reservation.requesterId || "",
    status: reservation.status || "",
    spaceId: reservation.spaceId || "",
    isFromSumarios: reservation.isFromSumarios || false,
    sumariosEventId: reservation.sumariosEventId || [],
    proctors: proctors,
    notes: notes,
    rules: rules,
  };
}

function getDurationInMinutes(startDate, endDate) {
  return Math.max(
    0,
    Math.round((endDate.getTime() - startDate.getTime()) / 60000)
  );
}

// Shift a real Date into the "UTC-as-local" frame that the stored times use.
// Stored times are written as e.g. 08:50+00:00 meaning "08:50 local", not
// 08:50 UTC. getTimezoneOffset() returns UTC-local in minutes (e.g. -60 for
// UTC+1), so subtracting it adds the local offset, making the UTC value of
// the result equal to the current local clock reading.
function toUTCAsLocal(d) {
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000);
}

function formatTimeOnly(date) {
  if (!date) return "";
  return date.toLocaleString("pt-PT", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC",
  });
}

function formatDateTimeDisplay(date) {
  if (!date) return "";
  return date.toLocaleString("pt-PT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone: "UTC",
  });
}

function formatRemainingTime(ms) {
  var totalMinutes = Math.floor(ms / 60000);
  var hours = Math.floor(totalMinutes / 60);
  var minutes = totalMinutes % 60;
  return pad(hours) + ":" + pad(minutes);
}

function pad(value) {
  return String(value).padStart(2, "0");
}

export default ExamsPage;
